/**
 * Password Reset API — token-based password reset for all users.
 */

/// <reference lib="dom" />

import { handleApiCall } from './errors';
import { getAccessToken } from './auth-token';

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables');
}

/**
 * Send a password reset email.
 * Does NOT require authentication — called by unauthenticated users.
 */
export async function sendPasswordResetDirect(email: string): Promise<{ success: boolean }> {
  return handleApiCall(async () => {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error ?? 'Failed to send password reset email');
    return data;
  });
}

/**
 * Validate a password reset token (used on the reset-password page to check if token is valid).
 * Uses the anon key — RLS policy allows public SELECT on password_reset_tokens.
 */
export async function getPasswordResetTokenDirect(token: string): Promise<{
  email: string;
  valid: boolean;
  error?: string;
}> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/password_reset_tokens?token=eq.${token}&select=email,expires_at,used`,
    {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
    }
  );

  if (!response.ok) return { email: '', valid: false, error: 'Network error' };

  const rows = await response.json();
  if (!rows || rows.length === 0) return { email: '', valid: false, error: 'Invalid reset link' };

  const token_data = rows[0];
  if (token_data.used) return { email: token_data.email, valid: false, error: 'Reset link already used' };
  if (new Date(token_data.expires_at) < new Date()) return { email: token_data.email, valid: false, error: 'Reset link expired' };

  return { email: token_data.email, valid: true };
}

/**
 * Reset password using a password reset token.
 * Does NOT require an existing session — called by unauthenticated users.
 */
export async function resetPasswordDirect(token: string, password: string): Promise<{ success: boolean }> {
  return handleApiCall(async () => {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ token, password }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error ?? `Password reset failed (${response.status})`);
    }
    return data;
  });
}

/**
 * Change password for authenticated users.
 * Requires authentication — called by logged-in users from settings.
 */
export async function changePasswordDirect(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    // Get current user email from session
    const userResponse = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!userResponse.ok) throw new Error('Failed to get current user');
    const userData = await userResponse.json();

    // Verify current password
    const verifyPasswordResponse = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        email: userData.email,
        password: currentPassword,
      }),
    });

    if (!verifyPasswordResponse.ok) {
      throw new Error('Current password is incorrect');
    }

    // Update password
    const updateResponse = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ password: newPassword }),
    });

    if (!updateResponse.ok) {
      const errorData = await updateResponse.json();
      throw new Error(errorData.error_description || 'Failed to update password');
    }

    return { success: true };
  });
}